import { isDefined, isNil } from "@clipboard-health/util-ts";
import { useGetClipboardScoreHistory } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useDynamicPenaltiesConfig } from "@src/appV2/ClipboardScore/hooks/featureFlags";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback } from "react";

import { ListLoadingState } from "../../../components/ListLoadingState";
import { PageWrapper } from "../../../components/PageWrapper";
import { useShowZeroScoreLogs } from "../../hooks/featureFlags";
import { useFetchSubstage } from "../../hooks/useFetchSubstage";
import { ClipboardScoreError } from "../ClipboardScoreError";
import { ClipboardScorePageContent, type ClipboardScorePageContentProps } from "./Content";
import { ClipboardScorePageHeader } from "./Header";

export function ClipboardScorePage() {
  const worker = useDefinedWorker();
  const { userId, attendance } = worker;
  const shiftsWorked = attendance?.completedShifts ?? 0;
  const {
    data: clipboardScoreProfile,
    isLoading: isClipboardScoreProfileLoading,
    refetch: refetchClipboardScoreProfile,
    isError: isClipboardScoreProfileError,
    error: clipboardScoreProfileError,
  } = useGetClipboardScoreProfile(userId, {
    enabled: isDefined(userId),
  });

  const {
    data: clipboardScoreHistory,
    isLoading: isClipboardScoreHistoryLoading,
    refetch: refetchClipboardScoreHistory,
    isError: isClipboardScoreHistoryError,
    error: clipboardScoreHistoryError,
  } = useGetClipboardScoreHistory({ userId }, { enabled: isDefined(userId) });

  const { enabled: isDynamicPenaltiesEnabled } = useDynamicPenaltiesConfig();
  const { data: substage } = useFetchSubstage();
  const showZeroScoreLogs = useShowZeroScoreLogs();

  const qualification = clipboardScoreProfile?.clipboardScoreRank?.qualification ?? "Workers";
  const { data } = useGetQualificationDisplayLabels([qualification]);
  const qualificationDisplayLabel = data[0] ?? qualification;

  const refreshAllData = useCallback(async () => {
    await Promise.all([refetchClipboardScoreProfile(), refetchClipboardScoreHistory()]);
  }, [refetchClipboardScoreProfile, refetchClipboardScoreHistory]);

  if (isClipboardScoreProfileLoading || isClipboardScoreHistoryLoading) {
    return <ListLoadingState texts={["Loading clipboard score..."]} />;
  }

  if (isClipboardScoreProfileError || isNil(clipboardScoreProfile)) {
    logError(APP_V2_APP_EVENTS.CLIPBOARD_SCORE_PROFILE_ERROR, {
      error: clipboardScoreProfileError,
      metadata: { workerId: userId },
    });
    return (
      <ClipboardScoreError
        title="Error loading clipboard score"
        onRefresh={refetchClipboardScoreProfile}
      />
    );
  }

  if (isClipboardScoreHistoryError || isNil(clipboardScoreHistory)) {
    logError(APP_V2_APP_EVENTS.CLIPBOARD_SCORE_HISTORY_ERROR, {
      error: clipboardScoreHistoryError,
      metadata: { workerId: userId },
    });
    return (
      <ClipboardScoreError
        title="Error loading clipboard score history"
        onRefresh={refetchClipboardScoreHistory}
      />
    );
  }

  let accessMetadata: ClipboardScorePageContentProps["accessMetadata"];
  if (isDefined(substage?.accessMetadata)) {
    accessMetadata = {
      basketSize: substage.accessMetadata.basketSize ?? 1,
      scoreThreshold: substage.accessMetadata.scoreThreshold ?? 150,
    };
  }

  logEvent(APP_V2_USER_EVENTS.CLIPBOARD_SCORE_PAGE_VIEWED, {
    workerId: userId,
  });

  const nextRestrictionDurationInDays = clipboardScoreProfile.restriction?.nextDuration ?? 7;
  const isFinalRestrictionNext =
    nextRestrictionDurationInDays === clipboardScoreProfile?.policy?.finalRestriction?.duration;
  const restrictionCount = clipboardScoreProfile.restrictionCount ?? 0;
  const finalRestrictionEvalWindow = clipboardScoreProfile.policy?.finalRestriction?.window ?? 0;

  const scoreLogsToShow = showZeroScoreLogs
    ? clipboardScoreHistory.scoreLogs
    : clipboardScoreHistory.scoreLogs.filter((log) => log.clipboardScoreAdjustment !== 0);

  return (
    <PageWrapper variant="tertiary">
      <ClipboardScorePageHeader
        clipboardScore={clipboardScoreProfile.clipboardScore}
        shiftsWorked={shiftsWorked}
        onRefresh={refreshAllData}
      />
      <ClipboardScorePageContent
        worker={worker}
        workerQualification={qualificationDisplayLabel}
        clipboardScoreProfile={clipboardScoreProfile}
        logs={scoreLogsToShow}
        isDynamicPenaltiesEnabled={isDynamicPenaltiesEnabled}
        accessMetadata={accessMetadata}
        isFinalRestrictionNext={isFinalRestrictionNext}
        nextRestrictionDurationInDays={nextRestrictionDurationInDays}
        restrictionCount={restrictionCount}
        finalRestrictionEvalWindow={finalRestrictionEvalWindow}
      />
    </PageWrapper>
  );
}
